import html from "solid-js/html";
import { createContext, useContext } from "solid-js";
import { FloatingRoot, FloatingTrigger, FloatingContent, useFloatingHover } from "./Floating.ts";

const POPOVER_CLS = "bg-white rounded-default shadow-lg border border-neutral-200";

interface PopoverProps {
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    placement?: string;
    offset?: number;
    // Nested floating that shouldn't close (or be closed by) an ancestor popover.
    standalone?: boolean;
    children?: unknown;
}

interface HoverPopoverProps extends PopoverProps {
    hoverDelay?: number;
    hoverCloseDelay?: number;
}

export function Popover(props: PopoverProps) {
    return (
        html`<${FloatingRoot} open=${() => props.open} onOpenChange=${(v: boolean) => props.onOpenChange?.(v)} placement=${() => props.placement ?? "bottom-start"} offset=${() => props.offset ?? 8} flip=${true} shift=${true} standalone=${() => props.standalone}>
            ${() => props.children}
        <//>`
    );
}

interface PopoverTriggerProps {
    class?: string;
    title?: string;
    children?: unknown;
}

export function PopoverTrigger(props: PopoverTriggerProps) {
    return (
        html`<${FloatingTrigger} openOnHover=${false} class=${() => props.class || ""} title=${() => props.title}>
            ${() => props.children}
        <//>`
    );
}

interface PopoverContentProps {
    class?: string;
    style?: Record<string, string | number>;
    children?: unknown;
}

export function PopoverContent(props: PopoverContentProps) {
    return (
        html`<${FloatingContent} class=${() => POPOVER_CLS + " " + (props.class || "")} style=${props.style}>
            ${() => props.children}
        <//>`
    );
}

export function HoverPopover(props: HoverPopoverProps) {
    return (
        html`<${FloatingRoot} open=${() => props.open} onOpenChange=${(v: boolean) => props.onOpenChange?.(v)} placement=${() => props.placement ?? "bottom-start"} offset=${() => props.offset ?? 8} flip=${true} shift=${true} standalone=${() => props.standalone}>
            <${HoverPopoverInner} hoverDelay=${() => props.hoverDelay ?? 0} hoverCloseDelay=${() => props.hoverCloseDelay ?? 150}>
                ${() => props.children}
            <//>
        <//>`
    );
}

interface HoverPopoverContextValue {
    hoverDelay: number;
    hoverCloseDelay: number;
}

const HoverPopoverContext = createContext<HoverPopoverContextValue | null>(null);

interface HoverPopoverInnerProps {
    hoverDelay: number;
    hoverCloseDelay: number;
    children?: unknown;
}

function HoverPopoverInner(props: HoverPopoverInnerProps) {
    return (
        html`<${HoverPopoverContext.Provider} value=${{
            get hoverDelay() { return props.hoverDelay; },
            get hoverCloseDelay() { return props.hoverCloseDelay; },
        }}>
            ${() => props.children}
        <//>`
    );
}

interface HoverPopoverTriggerProps {
    asChild?: boolean;
    class?: string;
    children?: unknown;
}

export function HoverPopoverTrigger(props: HoverPopoverTriggerProps) {
    const ctx = useContext(HoverPopoverContext);
    return (
        html`<${FloatingTrigger} openOnHover=${true} class=${() => props.class || ""} hoverDelay=${() => ctx?.hoverDelay ?? 0} hoverCloseDelay=${() => ctx?.hoverCloseDelay ?? 150}>
            ${() => props.children}
        <//>`
    );
}

export function HoverPopoverContent(props: PopoverContentProps) {
    const ctx = useContext(HoverPopoverContext);
    const hoverProps = useFloatingHover(true, ctx?.hoverCloseDelay ?? 150);

    return (
        html`<${FloatingContent} class=${() => POPOVER_CLS + " " + (props.class || "")} style=${props.style} onMouseEnter=${hoverProps.onMouseEnter} onMouseLeave=${hoverProps.onMouseLeave}>
            ${() => props.children}
        <//>`
    );
}
